import { useLeadStatuses } from "@/api/app-configuration.api";
import { useSyncedState } from "@/hooks/use-synced-state";
import { useIsAdmin } from "@/services/auth.service";
import { Campaign, LeadStatus } from "@/types/campaign";
import { api } from "@/utils/api";
import {
  formatWeekPeriod,
  getUTCWeekPeriod,
  UTCToLocalDate,
  UTCWeekPeriod,
  WEEKDAYS,
} from "@/utils/dates";
import { formatNumber } from "@/utils/number-utils";
import { isOneOf } from "@/utils/type-guards";
import { UTCDate } from "@date-fns/utc";
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { addWeeks, differenceInWeeks, setDay, startOfWeek } from "date-fns";
import { XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  composeRenderProps,
  DialogTrigger,
  Tooltip,
  TooltipTrigger,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  GraphBarProps,
  WeeklyDataPoint,
  WeeklyGraph,
} from "../common/WeeklyGraph";
import { Popover } from "../generic/Popover";
import { Separator } from "../generic/Separator";
import { composeTailwindRenderProps, focusRing } from "../generic/utils";
import { StatusIndicator } from "./StatusIndicator";
import { Wrap } from "./Wrap";

type LeadsOverviewResponse = {
  periods: {
    periodStart: string;
    periodEnd: string | null;
    counts: { status: string; count: number }[];
  }[];
  allTime: Partial<Record<string, number>>;
};

type LeadsScheduleResponse = {
  periods: {
    periodStart: string;
    periodEnd: string;
    counts: { campaignId: number; count: number }[];
  }[];
  allTime: null;
};

const CLOSED_STATUSES_ARRAY: Exclude<LeadStatus, "New">[] = [
  "Qualified",
  "Disqualified",
] as const;

const CLOSED_STATUSES = new Set<string>(CLOSED_STATUSES_ARRAY);

type DataPoint = WeeklyDataPoint & {
  week: UTCWeekPeriod;
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  countsByStatus: Map<string, number>;
  countsByCampaignId: Map<number, number>;
};

export type CampaignLeadsGraphProps = {
  campaigns?: Campaign[];
  isLoadingCampaigns?: boolean;
  isSingleCampaign?: boolean;
  title?: string;
  loadingText?: string;
  noDataText?: string;
  futureLeads?: number;
  selectedWeek?: UTCWeekPeriod | null;
  setSelectedWeek?: (week: UTCWeekPeriod | null) => void;
  onAdjustWeekSchedule?: (options: {
    campaignId: number;
    week: UTCWeekPeriod;
  }) => void;
  hideLegend?: boolean;
  alignment?: "center" | "left" | "right";
  baseDate?: UTCDate;
  minDate?: UTCDate;
  maxDate?: UTCDate;
};

export function CampaignLeadsGraph({
  campaigns,
  isLoadingCampaigns = false,
  isSingleCampaign = false,
  title,
  loadingText,
  noDataText,
  futureLeads,
  onAdjustWeekSchedule,
  hideLegend = false,
  alignment = "center",
  baseDate,
  minDate,
  maxDate,
  ...props
}: CampaignLeadsGraphProps) {
  campaigns = useMemo(() => campaigns ?? [], [campaigns]);

  const campaignsById = useMemo(
    () => new Map(campaigns.map((campaign) => [campaign.id, campaign])),
    [campaigns],
  );

  const campaignIds = useMemo(() => [...campaignsById.keys()], [campaignsById]);

  const currentWeekStart = useMemo(
    () => startOfWeek(new UTCDate(), { weekStartsOn: 1 }),
    [],
  );

  baseDate = useMemo(() => {
    if (baseDate) return baseDate;
    let newBaseDate = currentWeekStart;
    if (minDate && newBaseDate < minDate) {
      newBaseDate = minDate;
    }
    if (maxDate && newBaseDate > maxDate) {
      newBaseDate = maxDate;
    }
    return newBaseDate;
  }, [baseDate, currentWeekStart, maxDate, minDate]);

  const minWeek = useMemo(
    () => minDate && getUTCWeekPeriod(minDate),
    [minDate],
  );
  const maxWeek = useMemo(
    () => maxDate && getUTCWeekPeriod(maxDate),
    [maxDate],
  );

  const leadsQueryBaseParams = useMemo(() => {
    let periodCount = 64;

    const baseWeek = getUTCWeekPeriod(baseDate);

    if (minWeek && maxWeek) {
      const totalWeeks = differenceInWeeks(maxWeek.end, minWeek.start, {
        roundingMethod: "ceil",
      });

      if (totalWeeks < periodCount) {
        periodCount = totalWeeks;
      }
    }

    const minWeekOffset =
      minWeek &&
      Math.min(
        differenceInWeeks(minWeek.start, baseWeek.start, {
          roundingMethod: "ceil",
        }),
        0,
      );
    const maxWeekOffset =
      maxWeek &&
      Math.max(
        differenceInWeeks(maxWeek.start, baseWeek.start, {
          roundingMethod: "ceil",
        }),
        0,
      );

    let weekOffset = 0;

    let alignmentOffset = 0;
    if (alignment === "left") {
      alignmentOffset = 0;
    } else if (alignment === "center") {
      alignmentOffset = -Math.floor(periodCount / 2);
    } else if (alignment === "right") {
      alignmentOffset = -periodCount + 1;
    }

    let firstWeekOffset = alignmentOffset;
    let lastWeekOffset = alignmentOffset + periodCount - 1;

    const minOffsetOverflow =
      minWeekOffset !== undefined
        ? Math.max(minWeekOffset - firstWeekOffset, 0)
        : 0;

    const maxOffsetOverflow =
      maxWeekOffset !== undefined
        ? Math.max(lastWeekOffset - maxWeekOffset, 0)
        : 0;

    if (minOffsetOverflow || maxOffsetOverflow) {
      let adjustment = 0;
      if (isOneOf(alignment, "center", "left")) adjustment += minOffsetOverflow;
      if (isOneOf(alignment, "center", "right"))
        adjustment -= maxOffsetOverflow;

      weekOffset += adjustment;
      firstWeekOffset += adjustment;
      lastWeekOffset += adjustment;
    }

    const fromDate = addWeeks(baseWeek.start, firstWeekOffset);

    return {
      fromDate,
      periodCount,
    };
  }, [baseDate, minWeek, maxWeek, alignment]);

  const leadsQuery = useInfiniteQuery({
    queryKey: [
      "campaign-leads-overview",
      campaignIds,
      isSingleCampaign,
      leadsQueryBaseParams,
    ],
    initialPageParam: { pageNum: 0 },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: Infinity,
    queryFn: ({ pageParam, signal }) => {
      const { fromDate: baseFromDate, periodCount } = leadsQueryBaseParams;

      const fromDate = addWeeks(baseFromDate, pageParam.pageNum * periodCount);

      return Promise.all([
        api
          .post(
            `apps/sales/campaigns/overview/week?from=${fromDate.toISOString()}&periodCount=${periodCount}&grouping=leadStatus`,
            { json: campaignIds, signal },
          )
          .json<LeadsOverviewResponse>(),
        !isSingleCampaign
          ? api
              .post(
                `apps/sales/campaigns/schedule/week?from=${fromDate.toISOString()}&periodCount=${periodCount}&grouping=campaign`,
                { json: campaignIds, signal },
              )
              .json<LeadsScheduleResponse>()
          : null,
      ]).then(([overview, schedule]) => {
        const weeks = Array.from({ length: periodCount }, (_, i) => {
          const weekStart = addWeeks(fromDate, i);

          const weekPeriod = getUTCWeekPeriod(weekStart);

          let isPast = false;
          let isCurrent = false;
          let isFuture = false;

          if (weekStart.getTime() < currentWeekStart.getTime()) {
            isPast = true;
          } else if (weekStart.getTime() === currentWeekStart.getTime()) {
            isCurrent = true;
          } else {
            isFuture = true;
          }

          return {
            week: weekPeriod,
            isPast,
            isCurrent,
            isFuture,
            countsByStatus: new Map<string, number>(),
            countsByCampaignId: new Map<number, number>(),
          };
        });

        const weeksMap = new Map(
          weeks.map((point) => [point.week.start.getTime(), point]),
        );

        function addToCount<K>(map: Map<K, number>, key: K, value: number) {
          map.set(key, (map.get(key) ?? 0) + value);
        }

        for (const period of overview.periods) {
          const weekStart = startOfWeek(new UTCDate(period.periodStart), {
            weekStartsOn: 1,
          });

          const week = weeksMap.get(weekStart.getTime());
          if (!week) continue;

          for (const { status, count } of period.counts) {
            addToCount(week.countsByStatus, status, count);
          }
        }

        if (schedule) {
          for (const period of schedule.periods) {
            const weekStart = startOfWeek(new UTCDate(period.periodStart), {
              weekStartsOn: 1,
            });

            const week = weeksMap.get(weekStart.getTime());
            if (!week) continue;

            for (const { campaignId, count } of period.counts) {
              addToCount(week.countsByCampaignId, campaignId, count);
            }
          }
        }

        return {
          allTime: overview.allTime,
          periods: weeks,
        };
      });
    },
    getPreviousPageParam: (firstPage, _pages, firstPageParam) => {
      const firstPeriod = firstPage.periods.at(0);
      if (firstPeriod && minWeek && firstPeriod.week.start <= minWeek.start)
        return;
      return { pageNum: firstPageParam.pageNum - 1 };
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const lastPeriod = lastPage.periods.at(-1);
      if (lastPeriod && maxWeek && lastPeriod.week.start >= maxWeek.start)
        return;
      return { pageNum: lastPageParam.pageNum + 1 };
    },
    enabled: campaignIds.length > 0,
  });

  const {
    isFetching,
    fetchPreviousPage,
    hasPreviousPage,
    fetchNextPage,
    hasNextPage,
  } = leadsQuery;

  type LeadsData = typeof leadsQuery.data extends
    | InfiniteData<infer TData, unknown>
    | undefined
    ? TData
    : never;

  const leadsData = useMemo(
    () =>
      (leadsQuery.data ?? { pages: [], pageParams: [] }) as {
        pages: LeadsData[];
        pageParams: { pageNum: number }[];
      },
    [leadsQuery.data],
  );

  const { firstLoadedWeek, lastLoadedWeek } = useMemo(() => {
    const firstPage = leadsData.pages.at(0);
    const lastPage = leadsData.pages.at(-1);

    return {
      firstLoadedWeek: firstPage?.periods.at(0)?.week,
      lastLoadedWeek: lastPage?.periods.at(-1)?.week,
    };
  }, [leadsData.pages]);

  const allTime = useMemo(
    () => leadsData.pages.at(0)?.allTime,
    [leadsData.pages],
  );

  const allPeriods = useMemo(
    () => leadsData?.pages.flatMap((page) => page.periods) ?? [],
    [leadsData?.pages],
  );

  const data = useMemo(() => {
    return allPeriods.map((period): DataPoint => {
      const { week } = period;
      const countsByStatus = new Map<string, number>();
      const countsByCampaignId = new Map<number, number>();

      if (period.isFuture) {
        countsByStatus.set("Scheduled", 0);
      }

      for (const [status, count] of period.countsByStatus) {
        countsByStatus.set(status, count);
      }

      if ((period.isCurrent || period.isFuture) && !isSingleCampaign) {
        for (const campaign of campaigns) {
          if (campaign.type !== "Scheduled") continue;

          const startDate = campaign.chosenStartDate
            ? new UTCDate(campaign.chosenStartDate)
            : null;
          if (!startDate || startDate > week.end) continue;

          const endDate = campaign.chosenEndDate
            ? new UTCDate(campaign.chosenEndDate)
            : null;

          if (endDate && endDate < week.start) continue;

          countsByCampaignId.set(campaign.id, 0);
        }

        for (const [campaignId, count] of period.countsByCampaignId) {
          countsByCampaignId.set(campaignId, count);
        }
      }

      return {
        ...period,
        date: week.start,
        value: [...period.countsByStatus.values()].reduce(
          (sum, count) => sum + count,
          0,
        ),
        countsByStatus,
        countsByCampaignId,
      };
    });
  }, [allPeriods, campaigns, isSingleCampaign]);

  // const hasData = useMemo(
  //   () => data.length > 0 && data.some((point) => point.value !== 0),
  //   [data],
  // );
  // ? MWK (17-10-2025): Changed to look at allTime instead of data points for determining if there's data since data points can be empty due to date range filtering
  const hasData = useMemo(() => {
    if (!allTime) return false;
    return typeof allTime.total === "number" ? allTime.total > 0 : false;
  }, [allTime]);

  const [selectedWeek, setSelectedWeek] = useSyncedState(
    props.selectedWeek,
    props.setSelectedWeek,
    null,
  );

  const singleCampaign = useMemo(
    () => (isSingleCampaign && campaigns.length === 1 ? campaigns[0] : null),
    [isSingleCampaign, campaigns],
  );

  const isTriggerCampaign = useMemo(
    () => singleCampaign?.type === "Triggered",
    [singleCampaign],
  );

  const isActiveTriggerCampaign = useMemo(
    () => singleCampaign?.status === "Active",
    [singleCampaign],
  );

  function extractWeeklyLimit(c?: Campaign | null): number {
    return c?.maxLeadsPerWeek ?? 0;
  }

  const weeklyLimit = extractWeeklyLimit(singleCampaign);

  const [visibleWeeks, setVisibleWeeks] = useState<UTCWeekPeriod[]>([]);

  useEffect(() => {
    // An infinite query can only run one fetch at a time, and by default each
    // fetchNextPage/fetchPreviousPage call cancels the one in flight. When the
    // graph is wider than a page (ultrawide or zoomed out) both edges need data,
    // so fetching both directions here would cancel each other forever. Fetch one
    // page at a time and let the effect re-run once it lands.
    if (!visibleWeeks.length || isFetching) return;

    const firstVisibleWeek = visibleWeeks[0];
    const lastVisibleWeek = visibleWeeks[visibleWeeks.length - 1];

    if (
      hasPreviousPage &&
      firstLoadedWeek &&
      firstVisibleWeek.start < firstLoadedWeek.start
    ) {
      fetchPreviousPage({ cancelRefetch: false });
    } else if (
      hasNextPage &&
      lastLoadedWeek &&
      lastVisibleWeek.start > lastLoadedWeek.start
    ) {
      fetchNextPage({ cancelRefetch: false });
    }
  }, [
    fetchNextPage,
    fetchPreviousPage,
    firstLoadedWeek,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    lastLoadedWeek,
    visibleWeeks,
  ]);

  return (
    <div className="px-8 xl:px-14">
      {!hideLegend && (
        <Topbar
          futureLeads={futureLeads}
          statistics={leadsData?.pages[0]?.allTime ?? {}}
          title={title}
          isLoading={leadsData?.pages.length === 0}
        />
      )}

      <WeeklyGraph
        data={data}
        alignment={alignment}
        baseDate={baseDate}
        minDate={minDate}
        maxDate={maxDate}
        maxValue={weeklyLimit}
        isLoading={isLoadingCampaigns || isFetching}
        loadingText={loadingText}
        hasData={hasData}
        noDataText={noDataText}
        onVisibleWeeksChange={setVisibleWeeks}
        renderBar={(bar) => (
          <GraphBar
            {...bar}
            campaigns={campaigns}
            campaignId={isSingleCampaign ? campaignIds[0] : undefined}
            selectedWeek={selectedWeek}
            setSelectedWeek={setSelectedWeek}
            onAdjustWeekSchedule={onAdjustWeekSchedule}
            isTriggerCampaign={isTriggerCampaign}
            isActiveTriggerCampaign={isActiveTriggerCampaign}
            maxLeadsPerWeek={weeklyLimit}
            singleCampaign={singleCampaign}
          />
        )}
      />
    </div>
  );
}

function Topbar({
  statistics,
  title,
  futureLeads,
  isLoading = false,
}: {
  statistics: LeadsOverviewResponse["allTime"];
  title?: string;
  futureLeads?: number;
  isLoading?: boolean;
}) {
  const { leadStatusMap, getLeadStatusLabel } = useLeadStatuses();

  let handled = 0;
  const handledStatistics: Partial<Record<string, number>> = {};
  const unhandledCounts: Partial<Record<string, number>> = {};

  for (const leadStatus of leadStatusMap.values()) {
    const count = statistics[leadStatus.name];
    if (count === undefined) continue;

    if (leadStatus.name === "New") {
      unhandledCounts[leadStatus.name] = count;
    } else {
      handled += count;
      if (count) handledStatistics[leadStatus.name] = count;
    }
  }

  const remaining = statistics.New ?? 0;

  return (
    <div className="flex items-center justify-between">
      {title && (
        <h1 className="text-theme-on-background text-[11px] font-bold">
          {title}
        </h1>
      )}
      <div
        className={twMerge(
          "ml-auto flex gap-x-5 py-5 opacity-100 transition-opacity",
          isLoading && "opacity-0",
        )}
      >
        <>
          <TooltipTrigger delay={0} closeDelay={0}>
            <Button
              className={composeTailwindRenderProps(
                focusRing,
                "flex items-center gap-x-2",
              )}
            >
              <StatusIndicator status="Handled" />
              <span className="text-theme-on-background text-[11px] font-medium">
                Behandlede: {formatNumber(handled)}
              </span>
            </Button>
            <Tooltip
              className="colors-glass backdrop-blur-glass flex flex-col rounded-md p-6 outline-none"
              offset={8}
              placement="bottom"
            >
              <div className="flex flex-col">
                <table className="w-full border-separate border-spacing-y-2">
                  <thead>
                    <tr className="text-theme-on-background-variant text-[10px] font-normal">
                      <th className="pr-2 pb-2 text-left">Status</th>
                      <th className="pb-2 pl-2 text-right">Antal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(handledStatistics).map(
                      ([key, value = 0]) => (
                        <tr key={key}>
                          <td className="py-1 pr-2 text-[11px] font-bold">
                            <div className="flex items-center gap-x-2">
                              {getLeadStatusLabel(key)}
                            </div>
                          </td>
                          <td className="py-1 pl-2 text-right text-[11px] font-bold">
                            {formatNumber(value)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </Tooltip>
          </TooltipTrigger>

          <div className="flex items-center gap-x-2">
            <StatusIndicator status="Draft" />
            <span className="text-theme-on-background text-[11px] font-medium">
              Ubehandlede: {formatNumber(remaining)}
            </span>
          </div>
        </>

        {futureLeads !== undefined && (
          <div className="flex items-center gap-x-2">
            <StatusIndicator status="Future" />
            <span className="text-theme-on-background text-[11px] font-medium">
              Planlagt: {formatNumber(futureLeads)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function GraphBar({
  campaigns,
  week,
  data,
  maxValue,
  campaignId,
  selectedWeek,
  setSelectedWeek,
  onAdjustWeekSchedule,
  isTriggerCampaign = true,
  isActiveTriggerCampaign = false,
  maxLeadsPerWeek = 0,
  singleCampaign = null,
}: GraphBarProps<DataPoint> & {
  campaigns?: Campaign[];
  campaignId?: number;
  selectedWeek: UTCWeekPeriod | null;
  setSelectedWeek: (week: UTCWeekPeriod | null) => void;
  onAdjustWeekSchedule?: CampaignLeadsGraphProps["onAdjustWeekSchedule"];
  isTriggerCampaign?: boolean;
  maxLeadsPerWeek?: number;
  isActiveTriggerCampaign?: boolean;
  singleCampaign?: Campaign | null;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const isSelected = useMemo(
    () => week.start.getTime() === selectedWeek?.start.getTime(),
    [week, selectedWeek],
  );

  const segments = useMemo(() => {
    if (data.isFuture) {
      if (isTriggerCampaign && singleCampaign?.chosenEndDate) {
        const endDate = new UTCDate(singleCampaign.chosenEndDate);

        if (week.start > endDate) {
          return [];
        }
      }

      const futureValue = data.value;

      return [
        {
          count: futureValue,
          displayCount: maxLeadsPerWeek,
          className: isTriggerCampaign
            ? "bg-[#2A2C26] group-hover/bar:bg-theme-on-background/32  border-theme-on-background/40"
            : "bg-theme-on-background/15 group-hover/bar:bg-theme-on-background/24",
          openClassName: "bg-theme-primary",
          isFuture: true,
        },
      ];
    }

    let handledCount = 0;
    let scheduledCount = 0;
    let unhandledCount = 0;

    for (const [status, count] of data.countsByStatus) {
      if (CLOSED_STATUSES.has(status ?? "")) {
        handledCount += count;
      } else if (status === "Scheduled") {
        scheduledCount += count;
      } else {
        unhandledCount += count;
      }
    }

    return [
      {
        count: scheduledCount,
        displayCount: scheduledCount,
        className:
          "bg-theme-on-background/15 group-hover/bar:bg-theme-on-background/24",
        openClassName: "",
        isFuture: true,
      },
      {
        count: unhandledCount,
        displayCount: unhandledCount,
        className: "bg-gray-400 dark:bg-gray-600",
        openClassName: "",
        isFuture: false,
      },
      {
        count: handledCount,
        displayCount: handledCount,
        className: "bg-green-600 dark:bg-green-400",
        openClassName: "",
        isFuture: false,
      },
    ];
  }, [
    data.countsByStatus,
    data.isFuture,
    data.value,
    isTriggerCampaign,
    maxLeadsPerWeek,
    singleCampaign,
    week.start,
  ]);

  const openPopover = useCallback(() => {
    setIsOpen(true);
    setSelectedWeek(week);
  }, [setIsOpen, setSelectedWeek, week]);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setSelectedWeek(null);
  }, [setIsOpen, setSelectedWeek]);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        openPopover();
      } else {
        closePopover();
      }
    },
    [openPopover, closePopover],
  );

  const displayMaxValue = useMemo(() => {
    if (isTriggerCampaign && maxLeadsPerWeek > 0) {
      return Math.max(maxValue, maxLeadsPerWeek);
    }
    return maxValue;
  }, [maxValue, isTriggerCampaign, maxLeadsPerWeek]);

  return (
    <Wrap
      renderWrapper={(renderChildren) => (
        <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
          <Button
            className={composeTailwindRenderProps(
              focusRing,
              "group/bar relative flex h-full w-full cursor-pointer items-end justify-center",
            )}
            data-selected={isSelected ? "" : undefined}
          >
            {renderChildren()}
          </Button>

          <GraphBarPopover
            campaigns={campaigns}
            campaignId={campaignId}
            week={week}
            data={data}
            onAdjustWeekSchedule={onAdjustWeekSchedule}
            onClose={closePopover}
            isTriggerCampaign={isTriggerCampaign}
            maxLeadsPerWeek={maxLeadsPerWeek}
          />
        </DialogTrigger>
      )}
      renderChildren={() => (
        <div className="relative flex h-full w-3.5 flex-col justify-end">
          {segments.map((segment, index) => {
            let heightPercent = 0;

            if (displayMaxValue > 0 && segment.count >= 0) {
              heightPercent = (segment.count / displayMaxValue) * 100;
              if (
                isTriggerCampaign &&
                segment.isFuture &&
                isActiveTriggerCampaign
              ) {
                heightPercent = (segment.displayCount / displayMaxValue) * 100;
              }
              if (
                segment.isFuture &&
                segment.displayCount <= 0 &&
                isTriggerCampaign &&
                isActiveTriggerCampaign
              ) {
                heightPercent = (segment.count / displayMaxValue) * 100;
              }
            }

            return (
              <div
                key={index}
                className={twMerge(
                  "w-full transition-[background-color,opacity]",
                  segment.className,
                  isSelected && segment.openClassName,
                  isTriggerCampaign &&
                    segment.isFuture &&
                    isActiveTriggerCampaign &&
                    "opacity-60",
                )}
                style={{
                  height: `${heightPercent}%`,
                }}
              />
            );
          })}
        </div>
      )}
    />
  );
}

function GraphBarPopover({
  campaigns,
  campaignId,
  week,
  data,
  onAdjustWeekSchedule,
  onClose,
  isTriggerCampaign = false,
  maxLeadsPerWeek = 0,
}: {
  campaigns?: Campaign[];
  campaignId?: number;
  week: UTCWeekPeriod;
  data: DataPoint;
  onAdjustWeekSchedule?: CampaignLeadsGraphProps["onAdjustWeekSchedule"];
  onClose: () => void;
  isTriggerCampaign?: boolean;
  maxLeadsPerWeek?: number;
}) {
  const navigate = useNavigate();

  const { getLeadStatusLabel } = useLeadStatuses();

  const campaignsById = useMemo(
    () => new Map(campaigns?.map((campaign) => [campaign.id, campaign])),
    [campaigns],
  );

  const formattedWeek = useMemo(
    () =>
      formatWeekPeriod(UTCToLocalDate(week.start), UTCToLocalDate(week.end)),
    [week],
  );

  const isAdmin = useIsAdmin();

  const renderFutureCount = useCallback(
    (campaignId: number, count: number) => {
      const campaign = campaignsById.get(campaignId);

      if (!campaign) return null;

      let scheduleAmount = 0;
      let scheduleAmountWithChanges = 0;

      const dayOffsets = new Map<string, number>(
        WEEKDAYS.map(({ id }, i) => [id, (i + 1) % 7]),
      );

      for (const schedule of campaign.schedules) {
        const dayOffset = dayOffsets.get(schedule.dayOfWeek.toLowerCase());
        if (dayOffset === undefined) continue;

        const date = setDay(week.start, dayOffset, { weekStartsOn: 1 });
        const scheduleChange = schedule.scheduleChanges.find(
          (change) => new UTCDate(change.date).getTime() === date.getTime(),
        );

        scheduleAmount += schedule.amount;
        scheduleAmountWithChanges += scheduleChange?.amount ?? schedule.amount;
      }

      if (scheduleAmount !== scheduleAmountWithChanges) {
        return (
          <>
            <span className="text-theme-on-background-variant pr-3 line-through">
              {formatNumber(scheduleAmount)}
            </span>
            &nbsp;
            <span>{formatNumber(count)}</span>
          </>
        );
      }

      return formatNumber(count);
    },
    [campaignsById, week],
  );

  const renderTableButton = useCallback(
    (props: React.ComponentPropsWithRef<typeof Button>) => (
      <div className="-mx-3 -my-2 flex items-center justify-center">
        <Button
          {...props}
          className={composeRenderProps(props.className, (className) =>
            twMerge(
              "hover:bg-theme-on-background-hover w-full cursor-pointer rounded px-3 py-2 [text-align:inherit] text-[11px] font-bold",
              className,
            ),
          )}
        >
          {props.children}
        </Button>
      </div>
    ),
    [],
  );

  const { activeTriggerCampaigns, monitoredCampaigns, monitoredCampaignsSet } =
    useMemo(() => {
      const activeTriggerCampaigns: Campaign[] = [];
      const monitoredCampaigns: Campaign[] = [];

      if (campaignId === undefined && (data.isCurrent || data.isFuture)) {
        for (const campaign of campaigns ?? []) {
          const chosenStartDate = campaign.chosenStartDate
            ? new UTCDate(campaign.chosenStartDate)
            : null;
          const chosenEndDate = campaign.chosenEndDate
            ? new UTCDate(campaign.chosenEndDate)
            : null;

          if (
            !chosenStartDate ||
            chosenStartDate.getTime() > week.end.getTime()
          )
            continue;

          if (chosenEndDate && chosenEndDate.getTime() < week.start.getTime())
            continue;

          if (campaign.type === "Scheduled") {
            const estimatedEndDate = campaign.estimatedEndDate
              ? new UTCDate(campaign.estimatedEndDate)
              : null;
            if (
              !estimatedEndDate ||
              estimatedEndDate.getTime() >= week.start.getTime()
            )
              continue;
            monitoredCampaigns.push(campaign);
          } else if (campaign.type === "Triggered") {
            activeTriggerCampaigns.push(campaign);
          }
        }
      }

      return {
        activeTriggerCampaigns,
        activeTriggerCampaignsSet: new Set(activeTriggerCampaigns),
        monitoredCampaigns,
        monitoredCampaignsSet: new Set(monitoredCampaigns),
      };
    }, [
      campaignId,
      campaigns,
      data.isCurrent,
      data.isFuture,
      week.end,
      week.start,
    ]);

  const countsByStatus = useMemo(() => {
    const countsByStatus = new Map(data.countsByStatus);
    if (data.countsByCampaignId.size) {
      countsByStatus.delete("Scheduled");
    }
    return countsByStatus;
  }, [data.countsByCampaignId.size, data.countsByStatus]);

  const countsByCampaign = useMemo(() => {
    const countsByCampaign = new Map<Campaign, number>();
    for (const [campaignId, count] of data.countsByCampaignId) {
      const campaign = campaignsById.get(campaignId);
      if (!campaign) continue;
      if (count === 0 && monitoredCampaignsSet.has(campaign)) continue;
      countsByCampaign.set(campaign, count);
    }
    return countsByCampaign;
  }, [campaignsById, data.countsByCampaignId, monitoredCampaignsSet]);

  const tables = useMemo(() => {
    const tables: {
      key: string | number;
      columns: {
        key: string | number;
        name: string;
        className?: string;
      }[];
      rows: {
        key: string | number;
        columns: Partial<
          Record<
            string | number,
            {
              className?: string;
              render: () => React.ReactNode;
            }
          >
        >;
      }[];
    }[] = [];

    if (countsByStatus.size > 0) {
      tables.push({
        key: "countsByStatus",
        columns: [
          {
            key: "status",
            name: "Status",
            className: "text-left",
          },
          {
            key: "count",
            name: "Antal",
            className: "text-right",
          },
        ],
        rows: [...countsByStatus].map(([status, count]) => ({
          key: status,
          columns: {
            status: {
              render: () => (
                <div className="flex items-center gap-x-2">
                  {getLeadStatusLabel(status)}
                </div>
              ),
            },
            count: {
              render: () =>
                data.isFuture && campaignId !== undefined
                  ? onAdjustWeekSchedule && isAdmin
                    ? renderTableButton({
                        onPress: () => {
                          onAdjustWeekSchedule({
                            campaignId,
                            week,
                          });
                          onClose();
                        },
                        children: renderFutureCount(campaignId, count),
                      })
                    : renderFutureCount(campaignId, count)
                  : formatNumber(count),
            },
          },
        })),
      });
    }

    if (countsByCampaign.size > 0) {
      tables.push({
        key: "countsByCampaign",
        columns: [
          {
            key: "campaign",
            name: "Flowkampagner",
            className: "text-left",
          },
          {
            key: "count",
            name: data.isCurrent ? "Afventer" : "Antal",
            className: "text-right",
          },
        ],
        rows: [...countsByCampaign].map(([campaign, count]) => ({
          key: campaign.id,
          columns: {
            campaign: {
              render: () =>
                renderTableButton({
                  onPress: () => {
                    navigate({
                      to: "/campaigns/$id",
                      params: { id: campaign.id },
                    });
                  },
                  children: campaign.name,
                }),
            },
            count: {
              render: () =>
                onAdjustWeekSchedule && isAdmin
                  ? renderTableButton({
                      onPress: () => {
                        onAdjustWeekSchedule({
                          campaignId: campaign.id,
                          week,
                        });
                        onClose();
                      },
                      children: renderFutureCount(campaign.id, count),
                    })
                  : renderFutureCount(campaign.id, count),
            },
          },
        })),
      });
    }

    if (activeTriggerCampaigns.length > 0) {
      tables.push({
        key: "activeTriggerCampaigns",
        columns: [
          {
            key: "campaign",
            name: "Triggerkampagner",
            className: "text-left",
          },
        ],
        rows: activeTriggerCampaigns.map((campaign) => ({
          key: campaign.id,
          columns: {
            campaign: {
              render: () =>
                renderTableButton({
                  onPress: () => {
                    navigate({
                      to: "/campaigns/$id",
                      params: { id: campaign.id },
                    });
                  },
                  children: campaign.name,
                }),
            },
          },
        })),
      });
    }

    if (monitoredCampaigns.length > 0) {
      tables.push({
        key: "monitoredCampaigns",
        columns: [
          {
            key: "campaign",
            name: "Flowkampagner i overvågning",
            className: "text-left",
          },
        ],
        rows: monitoredCampaigns.map((campaign) => ({
          key: campaign.id,
          columns: {
            campaign: {
              render: () =>
                renderTableButton({
                  onPress: () => {
                    navigate({
                      to: "/campaigns/$id",
                      params: { id: campaign.id },
                    });
                  },
                  children: campaign.name,
                }),
            },
          },
        })),
      });
    }

    return tables;
  }, [
    activeTriggerCampaigns,
    campaignId,
    countsByStatus,
    countsByCampaign,
    data.isCurrent,
    data.isFuture,
    getLeadStatusLabel,
    isAdmin,
    monitoredCampaigns,
    navigate,
    onAdjustWeekSchedule,
    onClose,
    renderFutureCount,
    renderTableButton,
    week,
  ]);

  if (data.isFuture && isTriggerCampaign) {
    return (
      <Popover
        offset={4}
        placement="right"
        className="colors-glass backdrop-blur-glass flex flex-col rounded-md p-3 outline-none"
      >
        <div className="flex items-center justify-between gap-x-8 px-5 py-4">
          <span className="text-[12px] font-bold">{formattedWeek}</span>
        </div>

        <Separator className="my-3" />

        <div className="flex flex-col px-5 py-4">
          <div className="text-theme-on-background-variant text-[11px]">
            Op til {maxLeadsPerWeek} leads/uge
          </div>
        </div>
      </Popover>
    );
  }

  return (
    <Popover
      offset={4}
      placement="right"
      className="colors-glass backdrop-blur-glass flex flex-col overflow-y-auto rounded-md p-3 outline-none"
    >
      <div className="flex items-center justify-between gap-x-8 px-5 py-4">
        <span className="text-[12px] font-bold">Periode {formattedWeek}</span>

        <Button
          onPress={() => onClose()}
          className={composeTailwindRenderProps(
            focusRing,
            "-m-2 flex size-8 cursor-pointer items-center justify-center rounded-full",
          )}
        >
          <XIcon size={18} />
        </Button>
      </div>

      <Separator className="my-3" />

      <div className="flex flex-col gap-y-8 px-5 py-4">
        {tables.length ? (
          tables.map((table) => (
            <table key={table.key} className="w-full">
              <thead>
                <tr className="text-theme-on-background-variant text-[10px] font-normal *:px-3 *:first:pl-0 *:last:pr-0">
                  {table.columns.map((column) => (
                    <th key={column.key} className={twMerge(column.className)}>
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {table.rows.map((row) => (
                  <tr
                    key={row.key}
                    className="*:px-3 *:pt-4 *:first:pl-0 *:last:pr-0"
                  >
                    {table.columns.map((column) => {
                      const rowColumn = row.columns[column.key];

                      return (
                        <td
                          key={column.key}
                          className={twMerge(
                            "text-[11px] font-bold",
                            column.className,
                            rowColumn?.className,
                          )}
                        >
                          {rowColumn?.render()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ))
        ) : (
          <div className="text-theme-on-background-variant text-center text-[11px] font-medium">
            Ingen leads i den valgte uge
          </div>
        )}
      </div>
    </Popover>
  );
}
